import { useEffect, useState, type ReactNode } from 'react';
import { workerHistoryService, type WorkHistory } from '@/services/api/workerHistoryService';

const CAT_TONE: Record<string, string> = {
  Permanent: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  FTC: 'bg-sky-50 text-sky-700 ring-sky-200',
  ICA: 'bg-amber-50 text-amber-700 ring-amber-200',
  Casual: 'bg-rose-50 text-rose-700 ring-rose-200',
  Intern: 'bg-violet-50 text-violet-700 ring-violet-200',
  Learnership: 'bg-slate-100 text-slate-700 ring-slate-200',
};
const tone = (c?: string | null) => CAT_TONE[c || ''] || 'bg-slate-50 text-slate-600 ring-slate-200';
const fmt = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
const dur = (a?: string | null, b?: string | null) => {
  if (!a) return null;
  const s = new Date(a), e = b ? new Date(b) : new Date();
  const m = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
  return m >= 12 ? `${(m / 12).toFixed(1)} yr` : `${Math.max(m, 0)} mo`;
};

export default function WorkHistoryTimeline({ employeeId }: { employeeId: string }) {
  const [data, setData] = useState<WorkHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true); setError(null); setData(null);
    workerHistoryService.getWorkHistory(employeeId)
      .then((d) => { if (live) setData(d); })
      .catch((e) => { if (live) setError(e?.response?.data?.detail || e?.message || 'Failed to load'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [employeeId]);

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading work history…</div>;
  if (error) return <div className="p-6 text-sm text-rose-600">{error}</div>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-serif text-2xl text-heritage-blue">{data.display_name}</h2>
        <span className="text-sm text-slate-500 tabular-nums">PN {data.employee_id}</span>
        {data.current.employment_category && (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ring-1 ${tone(data.current.employment_category)}`}>
            {data.current.employment_category}
          </span>
        )}
        {data.current.worker_status && <span className="text-xs text-slate-500">{data.current.worker_status}</span>}
        {data.current.termination_date && (
          <span className="text-xs text-rose-600 tabular-nums">Terminated {fmt(data.current.termination_date)}</span>
        )}
      </div>

      <Section title="Employment periods" count={data.employments.length}>
        {data.employments.map((e, i) => (
          <Row key={i} current={e.is_current}>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ring-1 ${tone(e.employment_category)}`}>
              {e.employment_category || '—'}
            </span>
            <span className="tabular-nums text-slate-700">{fmt(e.start_date)} → {e.end_date ? fmt(e.end_date) : 'present'}</span>
            <span className="text-xs text-slate-400 tabular-nums">{dur(e.start_date, e.end_date)}</span>
            {e.grade_level && <span className="text-xs text-slate-400">grade {e.grade_level}</span>}
            {e.worker_type && <span className="text-xs text-slate-400">{e.worker_type}</span>}
          </Row>
        ))}
      </Section>

      <Section title="Seat journey" count={data.assignments.length}>
        {data.assignments.map((a, i) => (
          <Row key={i} current={a.is_current}>
            <span className="font-mono text-xs text-heritage-blue">{a.fo_position_id}</span>
            <span className="tabular-nums text-slate-700">{fmt(a.valid_from)} → {a.valid_to ? fmt(a.valid_to) : 'present'}</span>
            <span className="text-xs text-slate-400 tabular-nums">{dur(a.valid_from, a.valid_to)}</span>
            {a.reason_code && <span className="text-xs text-slate-400">{a.reason_code}</span>}
          </Row>
        ))}
      </Section>

      {data.changes.length > 0 && (
        <Section title="Recorded transitions" count={data.changes.length}>
          {data.changes.map((c, i) => (
            <Row key={i}>
              <span className="text-xs text-slate-500">{c.attribute}</span>
              <span className="tabular-nums text-slate-700">{c.from_value || '—'} → {c.to_value || '—'}</span>
              <span className="text-xs text-slate-400 tabular-nums">{fmt(c.observed_at)}</span>
            </Row>
          ))}
        </Section>
      )}
    </div>
  );
}

function Section({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-2">
        <h3 className="font-serif text-lg text-slate-800">{title}</h3>
        <span className="text-xs text-slate-400 tabular-nums">{count}</span>
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ current, children }: { current?: boolean; children: ReactNode }) {
  return (
    <div className={`flex flex-wrap items-center gap-3 text-sm rounded-lg px-3 py-2 ring-1 ${current ? 'bg-heritage-blue/5 ring-heritage-blue/20' : 'bg-white ring-slate-100'}`}>
      {children}
    </div>
  );
}
